import React, { useEffect, useState } from 'react';
import {
  catalogToolsList,
  catalogToolsUpsert,
  catalogToolsSoftDelete,
  catalogTranslateTool,
  catalogMarkToolReviewed,
  signUpload,
  putToSignedUrl,
  type CatalogToolRow,
} from '../services/apiService';
import { supported_languages } from '../i18n/translations';
import { useGlobalAdminOutletContext } from './GlobalAdminLayout';

type EditingState = {
  id?: string;
  tool_key: string;
  name_i18n: Record<string, string>;
  description_i18n: Record<string, string>;
  i18n_review: Record<string, string>;
  tool_type: 'tool' | 'supply';
  is_consumable: boolean;
  sort_order: number;
  is_active: boolean;
  image_url?: string | null;
};

const emptyI18nRecord = (): Record<string, string> => {
  const o: Record<string, string> = {};
  for (const { code } of supported_languages) {
    o[code] = '';
  }
  return o;
};

const emptyForm = (): EditingState => ({
  tool_key: '',
  name_i18n: emptyI18nRecord(),
  description_i18n: emptyI18nRecord(),
  i18n_review: {},
  tool_type: 'tool',
  is_consumable: false,
  sort_order: 0,
  is_active: true,
});

function mergeRowIntoForm(row: CatalogToolRow): EditingState {
  const name = emptyI18nRecord();
  const desc = emptyI18nRecord();
  const ni = row.name_i18n as Record<string, string> | null | undefined;
  const di = row.description_i18n as Record<string, string> | null | undefined;
  if (ni && typeof ni === 'object') {
    for (const { code } of supported_languages) {
      if (ni[code] != null) name[code] = String(ni[code]);
    }
  }
  if (name.en === '' && row.name) name.en = row.name;
  if (di && typeof di === 'object') {
    for (const { code } of supported_languages) {
      if (di[code] != null) desc[code] = String(di[code]);
    }
  }
  if (desc.en === '' && row.description) desc.en = row.description;
  return {
    id: row.id,
    tool_key: row.tool_key,
    name_i18n: name,
    description_i18n: desc,
    i18n_review: (row.i18n_review as Record<string, string>) || {},
    tool_type: (row.tool_type === 'supply' ? 'supply' : 'tool') as 'tool' | 'supply',
    is_consumable: row.is_consumable,
    sort_order: row.sort_order,
    is_active: row.is_active,
    image_url: row.image_url,
  };
}

function stripEmptyI18n(m: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(m)) {
    if (v != null && String(v).trim() !== '') out[k] = String(v).trim();
  }
  return out;
}

function needsReviewLangs(row: CatalogToolRow): string[] {
  const m = row.i18n_review;
  if (!m || typeof m !== 'object') return [];
  return Object.entries(m)
    .filter(([, status]) => status === 'needs_review')
    .map(([code]) => code);
}

const GlobalCatalogTools: React.FC = () => {
  const { refreshAnthropicUsage } = useGlobalAdminOutletContext();
  const [items, setItems] = useState<CatalogToolRow[]>([]);
  const [onlyNeedsReview, setOnlyNeedsReview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [markingReviewed, setMarkingReviewed] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const list = await catalogToolsList();
      setItems(list);
    } catch (e: any) {
      setErr(e?.message || 'Failed to load catalog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(emptyForm());
    setPendingFile(null);
  };

  const openEdit = (row: CatalogToolRow) => {
    setEditing(mergeRowIntoForm(row));
    setPendingFile(null);
  };

  const save = async () => {
    if (!editing) return;
    const name_i18n = stripEmptyI18n(editing.name_i18n);
    const description_i18n = stripEmptyI18n(editing.description_i18n);
    if (Object.keys(name_i18n).length === 0) {
      setErr('Enter at least one translated name (e.g. English).');
      return;
    }
    if (!editing.id) {
      const tk = editing.tool_key?.trim();
      if (!tk) {
        setErr('Tool key is required for new tools (e.g. vacuum_cleaner)');
        return;
      }
    }

    setSaving(true);
    setErr('');
    try {
      const basePayload = {
        id: editing.id,
        tool_key: editing.tool_key,
        name_i18n,
        description_i18n,
        image_url: editing.image_url ?? null,
        tool_type: editing.tool_type,
        is_consumable: !!editing.is_consumable,
        sort_order: Number(editing.sort_order) || 0,
        is_active: editing.is_active !== false,
      };

      let saved = await catalogToolsUpsert(basePayload);

      if (pendingFile && saved.id) {
        const ct = pendingFile.type || 'image/png';
        const signed = await signUpload({
          intent: 'catalog_tool_image',
          content_type: ct,
          catalog_id: saved.id,
        });
        await putToSignedUrl(signed.upload_url, signed.headers, pendingFile);
        saved = await catalogToolsUpsert({
          ...basePayload,
          id: saved.id,
          tool_key: saved.tool_key,
          image_url: signed.public_url,
        });
      }

      setEditing(null);
      setPendingFile(null);
      await load();
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const translate = async () => {
    if (!editing?.id) return;
    setTranslating(true);
    setErr('');
    try {
      const { item, failed } = await catalogTranslateTool(editing.id);
      setEditing(mergeRowIntoForm(item));
      if (failed.length > 0) setErr(`Translation failed for: ${failed.join(', ')}. Try again.`);
      await load();
      refreshAnthropicUsage();
    } catch (e: any) {
      setErr(e?.message || 'Translation failed');
    } finally {
      setTranslating(false);
    }
  };

  const markReviewed = async (lang: string) => {
    if (!editing?.id) return;
    setMarkingReviewed(lang);
    setErr('');
    try {
      const item = await catalogMarkToolReviewed(editing.id, lang);
      setEditing(mergeRowIntoForm(item));
      await load();
    } catch (e: any) {
      setErr(e?.message || 'Failed to mark reviewed');
    } finally {
      setMarkingReviewed(null);
    }
  };

  const deactivate = async (row: CatalogToolRow) => {
    if (!window.confirm(`Deactivate "${row.name}" in the catalog? Existing family copies stay; new syncs skip inactive rows.`)) return;
    setErr('');
    try {
      await catalogToolsSoftDelete({ id: row.id });
      await load();
    } catch (e: any) {
      setErr(e?.message || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Standard tools</h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm">
          Add names and descriptions for each supported language. Edits sync to all families except tools a family has
          customized.
        </p>
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
          {err}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex gap-2">
          <button type="button" className="btn btn-primary" onClick={openNew}>
            Add standard tool
          </button>
          <button type="button" className="btn btn-secondary" onClick={load} disabled={loading}>
            Refresh
          </button>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <input type="checkbox" checked={onlyNeedsReview} onChange={(e) => setOnlyNeedsReview(e.target.checked)} />
          Show only items needing review
        </label>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-dashboard-border dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="text-left p-3">Image</th>
                <th className="text-left p-3">Key</th>
                <th className="text-left p-3">Name (primary)</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Active</th>
                <th className="text-left p-3">Needs review</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items
                .filter((row) => !onlyNeedsReview || needsReviewLangs(row).length > 0)
                .map((row) => {
                  const pending = needsReviewLangs(row);
                  return (
                    <tr key={row.id} className="border-t border-gray-200 dark:border-gray-700">
                      <td className="p-3 w-16">
                        {row.image_url ? (
                          <img src={row.image_url} alt="" className="w-12 h-12 object-contain rounded bg-white" />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="p-3 font-mono text-xs">{row.tool_key}</td>
                      <td className="p-3">{row.name}</td>
                      <td className="p-3">{row.tool_type || '—'}</td>
                      <td className="p-3">{row.is_active ? 'yes' : 'no'}</td>
                      <td className="p-3">
                        {pending.length > 0 ? (
                          <span
                            className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-xs font-medium"
                            title={pending.join(', ')}
                          >
                            {pending.length} lang{pending.length > 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600">—</span>
                        )}
                      </td>
                      <td className="p-3 text-right space-x-2">
                        <button type="button" className="btn btn-secondary btn-small" onClick={() => openEdit(row)}>
                          Edit
                        </button>
                        {row.is_active && (
                          <button type="button" className="btn btn-warning btn-small" onClick={() => deactivate(row)}>
                            Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-3xl w-full p-6 shadow-xl border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">{editing.id ? 'Edit tool' : 'New tool'}</h2>
            <div className="space-y-4">
              {!editing.id && (
                <div>
                  <label className="block text-sm font-medium mb-1">Tool key (stable id)</label>
                  <input
                    className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-600"
                    value={editing.tool_key}
                    onChange={(e) => setEditing({ ...editing, tool_key: e.target.value })}
                    placeholder="e.g. scrub_brush"
                  />
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Names and descriptions by language</p>
                  {editing.id && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-small"
                      onClick={translate}
                      disabled={translating || saving || !editing.name_i18n.en?.trim()}
                      title={!editing.name_i18n.en?.trim() ? 'Save an English name first' : 'Auto-translate via Claude'}
                    >
                      {translating ? 'Translating…' : 'Translate to all languages'}
                    </button>
                  )}
                </div>
                {supported_languages.map(({ code, label }) => (
                  <div
                    key={code}
                    className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-gray-50/50 dark:bg-gray-800/50"
                  >
                    <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2">
                      <span>
                        {label} ({code})
                      </span>
                      {editing.i18n_review[code] === 'needs_review' && (
                        <>
                          <span className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-medium">
                            needs review
                          </span>
                          <button
                            type="button"
                            className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                            onClick={() => markReviewed(code)}
                            disabled={markingReviewed === code}
                          >
                            {markingReviewed === code ? 'Marking…' : 'Mark reviewed'}
                          </button>
                        </>
                      )}
                    </div>
                    <input
                      className="w-full border rounded px-3 py-2 mb-2 dark:bg-gray-800 dark:border-gray-600 text-sm"
                      placeholder="Name"
                      value={editing.name_i18n[code] ?? ''}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          name_i18n: { ...editing.name_i18n, [code]: e.target.value },
                        })
                      }
                    />
                    <textarea
                      className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-600 text-sm"
                      rows={2}
                      placeholder="Description (optional)"
                      value={editing.description_i18n[code] ?? ''}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          description_i18n: { ...editing.description_i18n, [code]: e.target.value },
                        })
                      }
                    />
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Kind</label>
                  <select
                    className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-600"
                    value={editing.tool_type}
                    onChange={(e) => setEditing({ ...editing, tool_type: e.target.value as 'tool' | 'supply' })}
                  >
                    <option value="tool">tool</option>
                    <option value="supply">supply</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Sort order</label>
                  <input
                    type="number"
                    className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-600"
                    value={editing.sort_order ?? 0}
                    onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value, 10) || 0 })}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!editing.is_consumable}
                  onChange={(e) => setEditing({ ...editing, is_consumable: e.target.checked })}
                />
                <span>Consumable</span>
              </label>
              <div>
                <label className="block text-sm font-medium mb-1">Image (PNG, WebP, JPEG, SVG)</label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={(e) => setPendingFile(e.target.files?.[0] || null)}
                />
                {editing.image_url && !pendingFile && (
                  <p className="text-xs text-gray-500 mt-1">Current: {editing.image_url}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalCatalogTools;
